// Cliente HTTP da API /api/v1. Erros do servidor viram ApiError com código e detalhes por campo.

export interface FieldIssue {
  field: string;
  message: string;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
  }
  get fieldIssues(): FieldIssue[] {
    return Array.isArray(this.details) ? (this.details as FieldIssue[]) : [];
  }
}

async function request<T>(method: string, url: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(`/api/v1${url}`, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new ApiError(0, 'network_error', 'Sem conexão com o servidor. Verifique a internet e tente novamente.');
  }
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = data?.error ?? {};
    if (res.status === 401 && url !== '/auth/login' && url !== '/auth/me') window.dispatchEvent(new Event('crm:unauthorized'));
    throw new ApiError(res.status, err.code ?? 'error', err.message ?? `Erro ${res.status}`, err.details);
  }
  return data as T;
}

export const api = {
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body: unknown = {}) => request<T>('POST', url, body),
  put: <T>(url: string, body: unknown) => request<T>('PUT', url, body),
  patch: <T>(url: string, body: unknown) => request<T>('PATCH', url, body),
  del: <T>(url: string) => request<T>('DELETE', url),
};

export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '');
  return entries.length ? `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)]))}` : '';
}

// ------------------------------------------------------------------ tipos compartilhados

export type Level = 'nenhum' | 'leitura' | 'carteira' | 'aprovacao' | 'total';

export interface Me {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  mustChangePassword: boolean;
  permissions: Record<string, Level>;
}

export interface Paged<T> {
  data: T[];
  meta: { page: number; pageSize: number; total: number; totalPages: number };
}

export interface UserRow {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  roleId: string;
  roleName: string;
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  lastInviteAt: string | null;
  version: number;
}

export interface Role {
  id: string;
  key: string | null;
  name: string;
  description: string | null;
  permissions: Record<string, Level>;
  locked: boolean;
  active: boolean;
  activeUsers: number;
  version: number;
}

export interface PermissionDef {
  key: string;
  label: string;
  group: string;
  stage: string;
  levels: Level[];
}

export interface AuditEvent {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorUserId: string | null;
  actorName: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
}

// ------------------------------------------------------------------ cadastros (Etapa 03)

export type Relationship = 'prospect' | 'cliente' | 'ex_cliente' | 'parceiro';
export const RELATIONSHIP_LABEL: Record<Relationship, string> = { prospect: 'Prospect', cliente: 'Cliente', ex_cliente: 'Ex-cliente', parceiro: 'Parceiro' };
export const DECISION_ROLE_LABEL: Record<string, string> = { decisor: 'Decisor', influenciador: 'Influenciador', usuario: 'Usuário', financeiro: 'Financeiro', tecnico: 'Técnico', outro: 'Outro' };
export const MODALITY_LABEL: Record<string, string> = { hora_avulsa: 'Hora avulsa', banco_horas: 'Banco de horas', projeto_fechado: 'Projeto fechado', assinatura_mensal: 'Assinatura mensal' };

export interface LookupValue {
  id: string;
  kind: 'origem' | 'segmento' | 'modulo_sienge' | 'motivo_perda';
  name: string;
  active: boolean;
  sortOrder: number;
  version: number;
}
export interface Service {
  id: string;
  code: string;
  category: string;
  name: string;
  modalities: string[];
  active: boolean;
  version: number;
}
export interface Catalog {
  services: Service[];
  origem: LookupValue[];
  segmento: LookupValue[];
  modulo_sienge: LookupValue[];
  motivo_perda: LookupValue[];
  modalities: string[];
}

export interface CompanyRow {
  id: string;
  legalName: string | null;
  tradeName: string | null;
  documentType: 'cpf' | 'cnpj' | null;
  document: string | null;
  relationship: Relationship;
  city: string | null;
  state: string | null;
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  ownerUserId: string;
  ownerName: string;
  segmentId: string | null;
  sourceId: string;
  archivedAt: string | null;
}

export interface CompanyContactLink {
  id: string;
  contactId: string;
  name: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  jobTitle: string | null;
  department: string | null;
  decisionRole: string | null;
  isSignatory: boolean;
  isPrimary: boolean;
  endedAt: string | null;
  version: number;
}

export interface Company extends Omit<CompanyRow, 'ownerName'> {
  ownerName: string;
  zip: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  district: string | null;
  website: string | null;
  systems: string | null;
  needs: string | null;
  notes: string | null;
  archiveReason: string | null;
  mergedIntoId: string | null;
  mergedInto: { id: string; name: string } | null;
  siengeModuleIds: string[];
  serviceIds: string[];
  contacts: CompanyContactLink[];
  attachmentCount: number;
  missingForContract: string[];
  can: { edit: boolean; archive: boolean; merge: boolean };
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface DuplicateHit {
  id: string;
  name: string;
  reason: string;
  ownerName: string;
  archived: boolean;
  visible: boolean;
}

export interface TimelineEvent {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  actorName: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
}

export interface Attachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  description: string | null;
  uploadedBy: string;
  uploadedByName: string | null;
  createdAt: string;
  proposalId?: string | null;
  proposalCode?: string | null;
}

export interface ContactRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  whatsapp: string | null;
  companies: { id: string; name: string | null; jobTitle: string | null }[];
}

// Envio de arquivo (multipart). O navegador define o boundary.
export async function upload<T>(url: string, file: File, fields: Record<string, string> = {}): Promise<T> {
  const form = new FormData();
  for (const [k, v] of Object.entries(fields)) form.append(k, v);
  form.append('file', file);
  let res: Response;
  try {
    res = await fetch(`/api/v1${url}`, { method: 'POST', credentials: 'same-origin', body: form });
  } catch {
    throw new ApiError(0, 'network_error', 'Sem conexão com o servidor.');
  }
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data?.error?.code ?? 'error', data?.error?.message ?? `Erro ${res.status}`, data?.error?.details);
  return data as T;
}

// ------------------------------------------------------------------ oportunidades (Etapa 04)

export type OppStatus = 'aberta' | 'suspensa' | 'ganha' | 'perdida';
export const OPP_STATUS_LABEL: Record<OppStatus, string> = { aberta: 'Aberta', suspensa: 'Suspensa', ganha: 'Ganha', perdida: 'Perdida' };

export interface Stage {
  id: string;
  pipelineId: string;
  name: string;
  position: number;
  kind: 'aberta' | 'ganha' | 'perdida';
  requirements: string[];
  active: boolean;
}
export interface Pipeline {
  id: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  active: boolean;
  version: number;
  stages: Stage[];
}

export interface OppCard {
  id: string;
  code: string;
  title: string;
  status: OppStatus;
  companyId: string;
  companyName: string;
  pipelineId: string;
  stageId: string;
  stageName?: string;
  stageEnteredAt: string;
  ownerUserId: string;
  ownerName: string;
  oneTimeValue: string;
  monthlyValue: string;
  nextActionAt: string | null;
  nextActionNote: string | null;
  expectedCloseDate: string | null;
  suspendedUntil: string | null;
  isCurrentClient: boolean;
  origin: string;
  services: string[];
  temperature: string;
  version: number;
}

export interface OppItem {
  id?: string;
  serviceId: string;
  modality: string;
  valueKind?: 'pontual' | 'mensal';
  amount: string;
  hours: string | null;
  months: number | null;
  description: string | null;
}

export interface Opportunity {
  id: string;
  code: string;
  title: string;
  status: OppStatus;
  companyId: string;
  company: { id: string; name: string; relationship: string } | null;
  contactId: string | null;
  contact: { id: string; name: string; email: string | null; phone: string | null } | null;
  pipelineId: string;
  stageId: string;
  stageEnteredAt: string;
  ownerUserId: string;
  ownerName: string;
  sourceId: string;
  origin: string;
  isCurrentClient: boolean;
  oneTimeValue: string;
  monthlyValue: string;
  expectedCloseDate: string | null;
  nextActionAt: string | null;
  nextActionNote: string | null;
  lastInteractionAt: string | null;
  lastInteractionSummary: string | null;
  need: string | null;
  fitNotes: string | null;
  scope: string | null;
  stakeholders: string | null;
  decisionProcess: string | null;
  pendingItems: string | null;
  suspendedReason: string | null;
  suspendedUntil: string | null;
  wonAt: string | null;
  wonEvidence: string | null;
  finalConditions: string | null;
  lostAt: string | null;
  lostReasonId: string | null;
  lostNotes: string | null;
  retakeOn: string | null;
  items: OppItem[];
  stages: Stage[];
  history: { id: string; fromStageId: string | null; toStageId: string; fromStatus: OppStatus | null; toStatus: OppStatus; note: string | null; secondsInFromStage: number | null; movedAt: string; movedByName: string | null }[];
  ahead: { stageId: string; name: string; missing: { key: string; message: string }[] }[];
  can: { edit: boolean };
  version: number;
}

// ------------------------------------------------------------------ atividades (Etapa 05)

export type ActivityType = 'tarefa' | 'reuniao' | 'ligacao' | 'visita' | 'mensagem' | 'retorno';
export const ACTIVITY_TYPE_LABEL: Record<ActivityType, string> = { tarefa: 'Tarefa', reuniao: 'Reunião', ligacao: 'Ligação', visita: 'Visita', mensagem: 'Mensagem', retorno: 'Retorno' };
export const ACTIVITY_STATUS_LABEL: Record<string, string> = { pendente: 'Pendente', concluida: 'Concluída', cancelada: 'Cancelada', reagendada: 'Reagendada' };
export const PRIORITY_LABEL: Record<string, string> = { baixa: 'Baixa', normal: 'Normal', alta: 'Alta', urgente: 'Urgente' };

export interface ActivityRow {
  id: string;
  type: ActivityType;
  subject: string;
  description: string | null;
  companyId: string | null;
  companyName: string | null;
  contactId: string | null;
  contactName: string | null;
  opportunityId: string | null;
  opportunityTitle: string | null;
  refType: string | null;
  refId: string | null;
  ownerUserId: string;
  ownerName: string;
  startAt: string | null;
  dueAt: string;
  priority: string;
  status: 'pendente' | 'concluida' | 'cancelada' | 'reagendada';
  result: string | null;
  completedAt: string | null;
  statusReason: string | null;
  rescheduledFromId: string | null;
  version: number;
  canEdit: boolean;
}

export interface AlertRow {
  id: string;
  kind: string;
  refType: string;
  refId: string;
  title: string;
  dueAt: string | null;
  createdAt: string;
  readAt: string | null;
  resolvedAt: string | null;
}

// ------------------------------------------------------------------ temperatura e saúde (Etapa 06)

export type TemperatureBand = 'nao_avaliado' | 'frio' | 'morno' | 'quente';
export const TEMPERATURE_LABEL: Record<TemperatureBand, string> = { nao_avaliado: 'Não avaliado', frio: 'Frio', morno: 'Morno', quente: 'Quente' };
export const HEALTH_LABEL: Record<string, string> = { nao_avaliado: 'Não avaliado', saudavel: 'Saudável', atencao: 'Atenção', em_risco: 'Em risco' };

export interface Temperature {
  score: number | null;
  band: TemperatureBand;
  effectiveBand: TemperatureBand;
  modelVersion: number;
  breakdown: {
    criteria: { key: string; label: string; max: number; points: number; reasons: { text: string; points: number }[] }[];
    adjustments: { text: string; points: number }[];
    insufficient: boolean;
    bands: { morno: number; quente: number };
  };
  manual: { band: TemperatureBand; reason: string; reviewOn: string; at: string } | null;
  history: { id: string; score: number | null; band: TemperatureBand; source: 'auto' | 'manual'; note: string | null; modelVersion: number; createdAt: string; createdByName: string | null }[];
}

export interface Health {
  score: number | null;
  band: string;
  factors: { label: string; text: string; points: number }[];
  note: string;
  ratings: { id: string; score: number; comment: string | null; ratedOn: string; createdByName: string | null }[];
}

// ------------------------------------------------------------------ propostas (Etapa 07)

export const PROPOSAL_STATUS_LABEL: Record<string, string> = { rascunho: 'Rascunho', enviada: 'Enviada', em_revisao: 'Em revisão', aceita: 'Aceita', recusada: 'Recusada', vencida: 'Vencida', substituida: 'Substituída' };
export const APPROVAL_LABEL: Record<string, string> = { nao_requer: 'Dentro do limite', pendente: 'Aguardando aprovação', aprovado: 'Desconto aprovado', recusado: 'Desconto recusado' };

export interface ProposalItem {
  id: string;
  serviceId: string;
  serviceName: string;
  modality: string;
  valueKind: 'pontual' | 'mensal';
  description: string | null;
  hours: string | null;
  quantity: string;
  unitPrice: string;
  gross: string;
  discount: string;
  total: string;
}

export interface ProposalVersionFull {
  id: string;
  number: number;
  status: string;
  introduction: string | null;
  scope: string | null;
  inclusions: string | null;
  exclusions: string | null;
  paymentTerms: string | null;
  validUntil: string | null;
  grossOneTime: string;
  discountOneTime: string;
  oneTimeTotal: string;
  grossMonthly: string;
  discountMonthly: string;
  monthlyTotal: string;
  discountPercent: string;
  installments: { dueDate: string; amount: string; description: string | null }[];
  recurrenceStart: string | null;
  recurrenceMonths: number | null;
  approvalStatus: string;
  approvalNote: string | null;
  sentAt: string | null;
  sentChannel: string | null;
  sentTo: string | null;
  acceptedAt: string | null;
  acceptedByName: string | null;
  acceptanceEvidence: string | null;
  refusedAt: string | null;
  refusalReason: string | null;
  version: number;
  items: ProposalItem[];
  projection: string | null;
  problems: string[];
}

export interface Proposal {
  id: string;
  code: string;
  title: string;
  status: string;
  opportunityId: string;
  companyId: string;
  companyName: string;
  opportunity: { title: string; code: string; status: string };
  acceptedVersionId: string | null;
  versions: { id: string; number: number; status: string; oneTimeTotal: string; monthlyTotal: string; validUntil: string | null; sentAt: string | null; acceptedAt: string | null; approvalStatus: string }[];
  current: ProposalVersionFull | null;
  can: { edit: boolean; approve: boolean };
}
